import type {CodeSection} from '@github-ui/code-nav'
import type {StylingDirectivesLine} from '@github-ui/code-view-types'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useIsSafari} from '@github-ui/use-sticky-header/useIsSafari'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import React, {useLayoutEffect, useRef, useState} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useCodeLineIntersectionObservers} from '../../../../hooks/use-code-line-observer'
import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {useStickyHeaderHeight} from '../../../../hooks/use-sticky-header-height'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {calculateHighlightOffsetAndWidth, type HighlightPosition, useIsLineCollapsed} from '../../../../utilities/lines'
import {CopilotButton, type CopilotButtonHandle} from '../CopilotButton'
import {HighlightedOverlay} from '../HighlightedOverlay'
import {ExpandRowEllipsis} from './CodeFoldingEllipsisOverlay'
import {useHighlightedLinesInfo} from './HighlightedLineProvider'
import type {CodeLineData} from './hooks/use-code-lines'
import {useFocusedSearchResult, useSearchResults} from './SearchResultsProvider'
import {SyntaxHighlightedLine} from './SyntaxHighlightedLine'

const noop = () => {}

interface CodeLineProps {
  id?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  codeLineData: CodeLineData
  codeLineClassName?: string
  paddingLeft?: string
  setIsCollapsed?: (isCollapsed: boolean) => void
  onLineStickOrUnstick?: SetStickyLinesType
  highlightedTerm?: string
  codeLineToSectionMap?: Map<number, CodeSection[]> | undefined
  virtualOffset?: number
  virtualKey?: number | string
  forceVisible?: boolean
  stylingDirectivesLine?: StylingDirectivesLine
  measureRef?: (el: HTMLElement | null) => void
  virtualIndex?: number
  shouldUseCursor?: boolean
  copilotAccessAllowed: boolean
  shouldUseInert: boolean
}

export const CodeLine = React.memo(CodeLineUnmemoized)

function CodeLineUnmemoized({
  codeLineData,
  codeLineClassName,
  id,
  onClick,
  setIsCollapsed,
  onLineStickOrUnstick,
  virtualOffset,
  codeLineToSectionMap,
  stylingDirectivesLine,
  virtualKey,
  forceVisible,
  measureRef,
  virtualIndex,
  copilotAccessAllowed,
  shouldUseInert,
}: CodeLineProps) {
  const {lineNumber, rawText} = codeLineData
  const collapsed = useIsLineCollapsed(lineNumber)
  const {tabSize} = useCurrentBlob()
  const highlightedInfo = useHighlightedLinesInfo(lineNumber)
  const fileLineRef = useRef<HTMLDivElement>(null)

  // Measure highlight position in useLayoutEffect (after DOM commit) to avoid
  // getBoundingClientRect() layout reads during render. The double-render from
  // setState only affects highlighted lines (typically 1-20), not every line,
  // and runs synchronously before paint so there's no visual flicker.
  const [highlightPosition, setHighlightPosition] = useState<HighlightPosition | undefined>(undefined)
  useLayoutEffect(() => {
    if (!highlightedInfo || !fileLineRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHighlightPosition(undefined)
      return
    }
    setHighlightPosition(
      calculateHighlightOffsetAndWidth(highlightedInfo, fileLineRef.current, lineNumber, tabSize, rawText ?? ''),
    )
  }, [highlightedInfo, lineNumber, tabSize, rawText])

  const canDoStickyLines = true
  const numParents = codeLineToSectionMap?.get(lineNumber)?.length ?? 0
  const stickyHeaderHeight = useStickyHeaderHeight()
  const lineRefCallbackForObserver = useCodeLineIntersectionObservers(
    codeLineData,
    canDoStickyLines,
    stickyHeaderHeight,
    onLineStickOrUnstick ?? noop,
    numParents,
  )

  const wrapOption = useCodeViewOptions().codeWrappingOption

  const overlaySymbols = useSearchResults(lineNumber)
  const focusedSymbol = useFocusedSearchResult(lineNumber)

  const currentLineHeight = useCurrentLineHeight('react-line-numbers')

  const isFirstHighlightedLine = highlightedInfo?.start.line === lineNumber

  const menuAnchorRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<CopilotButtonHandle>(null)

  useLayoutEffect(() => {
    if (isFirstHighlightedLine) {
      menuRef.current?.setAnchor(menuAnchorRef.current)
    }
  }, [isFirstHighlightedLine])

  return (
    // TODO: this is not yet accessible.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={element => {
        measureRef?.(element)
        lineRefCallbackForObserver(element as HTMLTableRowElement | null)
      }}
      data-key={virtualKey}
      data-index={virtualIndex}
      className={clsx(
        codeLineClassName,
        'react-code-text react-code-line-contents',
        virtualOffset && 'virtual',
        wrapOption.enabled && measureRef && 'react-code-text-cell',
      )}
      style={{
        transform: virtualOffset ? `translateY(${virtualOffset}px)` : undefined,
        minHeight: wrapOption.enabled ? currentLineHeight : 'auto',
      }}
      onClick={onClick}
    >
      <div ref={menuAnchorRef}>
        {highlightedInfo && <HighlighterElement lineNumber={lineNumber} highlightPosition={highlightPosition} />}

        {collapsed && !highlightedInfo && (
          <HighlighterElement subtle lineNumber={lineNumber} highlightPosition={highlightPosition} />
        )}

        {overlaySymbols && overlaySymbols.length > 0 && (
          <HighlightedOverlay
            symbols={overlaySymbols}
            focusedSymbol={focusedSymbol}
            sx={{paddingLeft: '10px', width: 'auto'}}
            lineNumber={lineNumber}
          />
        )}
        <SyntaxHighlightedLine
          id={id}
          lineNumber={lineNumber}
          stylingDirectivesLine={stylingDirectivesLine}
          current={!!highlightedInfo}
          rawText={rawText}
          forceVisible={forceVisible}
          ref={fileLineRef}
          shouldUseInert={shouldUseInert}
        />
        {wrapOption.enabled && (
          <ExpandRowEllipsis
            codeLineData={codeLineData}
            setIsCollapsed={setIsCollapsed}
            onLineStickOrUnstick={onLineStickOrUnstick}
            leftOffset={0}
          />
        )}
        {isFirstHighlightedLine && copilotAccessAllowed && (
          <CopilotButton
            ref={menuRef}
            rowBeginNumber={highlightedInfo.start.line}
            rowEndNumber={highlightedInfo.end.line}
            id="code-line-copilot-button"
          />
        )}
      </div>
    </div>
  )
}

export function HighlighterElement({
  lineNumber,
  highlightPosition,
  subtle,
}: {
  lineNumber: number
  highlightPosition?: HighlightPosition
  subtle?: boolean
}) {
  //on safari the highlights are weirdly offset from the text area highlights, so we add a -3px bump to them
  const isSafari = useIsSafari()

  const hasOffset = highlightPosition?.offset !== undefined
  const hasWidth = highlightPosition?.width !== undefined
  const offset = highlightPosition?.offset ?? -72 // 72 is the width of the line number column ($lineNumberWidth)
  const width = highlightPosition?.width ?? 0

  // 82 is the width of the line number column ($lineNumberWidth) + 10px padding
  const widthWithOffset = hasOffset && hasWidth ? width : width + 82

  // 72 is the width of the line number column ($lineNumberWidth) that we are offsetting
  const defaultWidth = `calc(100% + 72px)`

  return (
    <Box
      sx={{
        position: 'absolute',
        backgroundColor: subtle ? 'neutral.subtle' : 'var(--bgColor-attention-muted, var(--color-attention-subtle))',
        height: '100%',
        opacity: '.6',
        boxShadow: subtle
          ? 'inset 2px 0 0 var(--fgColor-muted,  var(--color-fg-subtle))'
          : 'inset 2px 0 0 var(--fgColor-attention, var(--color-attention-fg))',
        top: isSafari ? '-3px' : 0,
        left: `${offset}px`,
        width: hasWidth ? `${widthWithOffset}px` : defaultWidth,
        // Allow clicks to pass through this element to the underlying code line
        pointerEvents: 'none',
      }}
      key={`highlighted-line-${lineNumber}`}
    />
  )
}
